import { useEffect, useState } from 'react'

import { supportService } from '@/services'

const SITE_LOCATIONS = ['Menu', 'OrderDetails']

// El catálogo de soporte es el mismo para toda la sesión (depende del catálogo y los roles del
// token, más el idioma), así que se cachea por `catalogId:lang`: el shell se desmonta y remonta al
// entrar y salir de las vistas con FullscreenLayerLayout (configuradores, gestión de usuarios) y no
// tiene sentido repetir las llamadas. Los fallos NO se cachean: se reintentan al remontar.
const cache = new Map()
const inflight = new Map()

const indexByKey = (rows) => {
	const byKey = {}
	for (const row of rows) byKey[row.key] = row
	return byKey
}

const fetchByLocation = async (lang) => {
	const responses = await Promise.all(
		SITE_LOCATIONS.map((siteLocation) => supportService.fetchSupportTypes({ siteLocation, lang }))
	)
	const byLocation = {}
	SITE_LOCATIONS.forEach((siteLocation, i) => {
		byLocation[siteLocation] = indexByKey(responses[i].data || [])
	})
	return byLocation
}

/**
 * DV-6593 — tipos de soporte por siteLocation, indexados por `key`, para la sección «Contáctanos»
 * del sidebar. Espejo de `professional-area/src/bff/support-types.ts`.
 *
 * El endpoint exige token, así que `enabled` debe esperar a tener cuenta. Llamar SOLO una vez por
 * shell: hay dos instancias de ProfessionalSidebar montadas (escritorio y cajón móvil) y un hook
 * por instancia duplicaría las peticiones.
 */
export default function useSupportTypes({ enabled, catalogId, lang }) {
	const [byLocation, setByLocation] = useState(null)

	useEffect(() => {
		if (!enabled || !lang) return undefined

		const key = `${catalogId}:${lang}`
		if (cache.has(key)) {
			setByLocation(cache.get(key))
			return undefined
		}

		let cancelled = false
		const request = inflight.get(key) || fetchByLocation(lang)
		inflight.set(key, request)

		request
			.then((result) => {
				cache.set(key, result)
				if (!cancelled) setByLocation(result)
			})
			// Sin tipos, el sidebar pinta el grupo de soporte de siempre (ver sidebarContactGroup).
			.catch(() => { if (!cancelled) setByLocation(null) })
			.finally(() => inflight.delete(key))

		return () => { cancelled = true }
	}, [enabled, catalogId, lang])

	return byLocation
}
